import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus, MoreVertical, Smartphone, Apple, Chrome } from 'lucide-react';

const InstallAppPrompt = ({ onClose }) => {
  const [platform, setPlatform] = useState('unknown');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        onClose();
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions
      setShowInstructions(true);
    }
  };

  // iOS Instructions Component
  const IOSInstructions = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-blue-400 font-semibold">
        <Apple className="w-5 h-5" />
        <span>Instrucciones para iPhone/iPad</span>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-start gap-4 bg-slate-800/50 p-4 rounded-xl">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold shrink-0">1</div>
          <div>
            <p className="font-medium text-white">Toca el botón de Compartir</p>
            <p className="text-sm text-white/60 mt-1">Busca el ícono <Share className="w-4 h-4 inline text-blue-400" /> en la barra inferior de Safari</p>
          </div>
        </div>

        <div className="flex items-start gap-4 bg-slate-800/50 p-4 rounded-xl">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold shrink-0">2</div>
          <div>
            <p className="font-medium text-white">Desliza hacia abajo</p>
            <p className="text-sm text-white/60 mt-1">Busca la opción "Agregar a pantalla de inicio"</p>
          </div>
        </div>

        <div className="flex items-start gap-4 bg-slate-800/50 p-4 rounded-xl">
          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold shrink-0">3</div>
          <div>
            <p className="font-medium text-white">Toca "Agregar"</p>
            <p className="text-sm text-white/60 mt-1">La app se instalará en tu pantalla de inicio</p>
          </div>
        </div>
      </div>

      {/* Visual Guide */}
      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 p-4 rounded-xl border border-blue-500/30">
        <div className="flex items-center justify-center gap-4">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <Share className="w-6 h-6 text-blue-400" />
            </div>
            <span className="text-xs text-white/60 mt-1">Compartir</span>
          </div>
          <span className="text-white/40">→</span>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <Plus className="w-6 h-6 text-blue-400" />
            </div>
            <span className="text-xs text-white/60 mt-1">Agregar</span>
          </div>
          <span className="text-white/40">→</span>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🐸</span>
            </div>
            <span className="text-xs text-white/60 mt-1">¡Listo!</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Android Instructions Component
  const AndroidInstructions = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-green-400 font-semibold">
        <Smartphone className="w-5 h-5" />
        <span>Instrucciones para Android</span>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-start gap-4 bg-slate-800/50 p-4 rounded-xl">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold shrink-0">1</div>
          <div>
            <p className="font-medium text-white">Toca el menú de Chrome</p>
            <p className="text-sm text-white/60 mt-1">Busca los 3 puntos <MoreVertical className="w-4 h-4 inline text-green-400" /> en la esquina superior</p>
          </div>
        </div>

        <div className="flex items-start gap-4 bg-slate-800/50 p-4 rounded-xl">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold shrink-0">2</div>
          <div>
            <p className="font-medium text-white">Selecciona "Instalar app" o "Agregar a pantalla"</p>
            <p className="text-sm text-white/60 mt-1">Puede aparecer como "Instalar Mr Frogs"</p>
          </div>
        </div>

        <div className="flex items-start gap-4 bg-slate-800/50 p-4 rounded-xl">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold shrink-0">3</div>
          <div>
            <p className="font-medium text-white">Confirma la instalación</p>
            <p className="text-sm text-white/60 mt-1">Toca "Instalar" en el mensaje que aparece</p>
          </div>
        </div>
      </div>

      {/* Visual Guide */}
      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 p-4 rounded-xl border border-green-500/30">
        <div className="flex items-center justify-center gap-4">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <MoreVertical className="w-6 h-6 text-green-400" />
            </div>
            <span className="text-xs text-white/60 mt-1">Menú</span>
          </div>
          <span className="text-white/40">→</span>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <Download className="w-6 h-6 text-green-400" />
            </div>
            <span className="text-xs text-white/60 mt-1">Instalar</span>
          </div>
          <span className="text-white/40">→</span>
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🐸</span>
            </div>
            <span className="text-xs text-white/60 mt-1">¡Listo!</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-slate-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl overflow-hidden shadow-2xl border-t sm:border border-white/10 animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-white relative overflow-hidden">
          <div className="absolute top-2 right-2">
            <button 
              onClick={onClose}
              className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-4xl">🐸</span>
            </div>
            <div>
              <h2 className="text-xl font-bold">Mr Frogs Mariscos</h2>
              <p className="text-white/80 text-sm">Instala nuestra app gratis</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showInstructions ? (
            <>
              {/* Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-white/80">
                  <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                    <span>🚀</span>
                  </div>
                  <span className="text-sm">Acceso rápido desde tu pantalla</span>
                </div>
                <div className="flex items-center gap-3 text-white/80">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                    <span>⭐</span>
                  </div>
                  <span className="text-sm">Acumula puntos y gana premios</span>
                </div>
                <div className="flex items-center gap-3 text-white/80">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                    <span>🔔</span>
                  </div>
                  <span className="text-sm">Recibe ofertas exclusivas</span>
                </div>
                <div className="flex items-center gap-3 text-white/80">
                  <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                    <span>📱</span>
                  </div>
                  <span className="text-sm">Funciona sin internet</span>
                </div>
              </div>

              {/* Install Button */}
              <button
                onClick={handleInstallClick}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2 text-lg"
              >
                <Download className="w-5 h-5" />
                {deferredPrompt ? 'Instalar App' : 'Ver cómo instalar'}
              </button>

              {/* Skip */}
              <button
                onClick={onClose}
                className="w-full py-3 text-white/50 hover:text-white/70 transition-colors text-sm mt-3"
              >
                Ahora no, seguir en el navegador
              </button>
            </>
          ) : (
            <>
              {/* Back button */}
              <button
                onClick={() => setShowInstructions(false)}
                className="text-white/60 hover:text-white mb-4 text-sm flex items-center gap-1"
              >
                ← Volver
              </button>

              {/* Platform specific instructions */}
              {platform === 'ios' && <IOSInstructions />}
              {platform === 'android' && <AndroidInstructions />}
              {platform === 'desktop' && (
                <div className="space-y-4">
                  <p className="text-white/80 text-center">
                    Visita esta página desde tu celular para instalar la app
                  </p>
                  <div className="flex justify-center gap-8">
                    <div className="text-center">
                      <Apple className="w-12 h-12 text-white/40 mx-auto" />
                      <span className="text-xs text-white/40 mt-1 block">iPhone</span>
                    </div>
                    <div className="text-center">
                      <Smartphone className="w-12 h-12 text-white/40 mx-auto" />
                      <span className="text-xs text-white/40 mt-1 block">Android</span>
                    </div>
                  </div>
                </div>
              )}
              {platform === 'unknown' && (
                <div className="space-y-6">
                  <IOSInstructions />
                  <div className="border-t border-white/10 pt-6">
                    <AndroidInstructions />
                  </div>
                </div>
              )}

              {/* Close button */}
              <button
                onClick={onClose}
                className="w-full py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors mt-6"
              >
                Entendido
              </button>
            </>
          )}
        </div>

        {/* Safe area for iPhone */}
        <div className="h-6 sm:hidden" />
      </div>
    </div>
  );
};

export default InstallAppPrompt;
